import moment, { Moment } from "moment";

type TimeInput = string | Date | Moment;

interface TimestampFormatter {
  pattern: string;
  (time: TimeInput): string;
}
// 支援的輸入格式清單（可依需求擴充）
const supportedFormats = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD",
  "YYYY/MM/DD HH:mm:ss",
  "YYYY/MM/DD HH:mm",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "MM-DD-YYYY",
  moment.ISO_8601,
];

// 過濾後端防衛性日期（ 9999-12-31 ），超過50年就視為非正常效期區間
const SENTINEL_YEARS_THRESHOLD = 50;

const timeValidation = (time: TimeInput): Moment => {
  let parsedTime: Moment;
  if (moment.isMoment(time)) {
    parsedTime = time;
  } else if (time instanceof Date) {
    parsedTime = moment(time);
  } else if (typeof time === "string") {
    parsedTime = moment(time, supportedFormats, true); // 嚴格模式
  } else {
    throw new Error("Invalid time input type.");
  }
  if (!parsedTime.isValid()) {
    throw new Error("Invalid date/time value.");
  }
  return parsedTime;
};

/**
 * 格式化時間函式
 * @param pattern - 輸出格式，預設為 "YYYY-MM-DD HH:mm:ss"
 * @returns TimestampFormatter - 格式化函數 => TimestampFormatter(time: string | Date | moment.Moment): string
 */
const timestampFormatter = (pattern = "YYYY-MM-DD HH:mm:ss"): TimestampFormatter => {
  const formatter = ((time: TimeInput): string => {
    const parsedTime: Moment = timeValidation(time);
    return parsedTime.format(formatter.pattern);
  }) as TimestampFormatter;

  formatter.pattern = pattern;
  return formatter;
};

/**
 * 格式化時間為指定的格式
 * 支援輸入: string、Date、Moment
 * 預設輸出格式為："YYYY-MM-DD HH:mm:ss"
 */
const formatToTimestamp = (time: string | Date | Moment, outputFormat = "YYYY-MM-DD HH:mm:ss"): string => {
  const parsedTime: Moment = timeValidation(time);
  return parsedTime.format(outputFormat);
};

/**
 * 獲取禁用日期
 * @param current - 當前日期
 * @param disableBase - 是否禁用基準日期
 * @param baseDate - 基準日期(預設為今天)
 * @returns 是否禁用
 */
const getDisabledDate = (current: Moment | null, { disableBase = false, baseDate = moment().endOf("day") }) => {
  if (!current) return false;

  // 使用 clone() 避免修改原始 baseDate 物件
  const base = baseDate.clone().startOf("day");

  if (disableBase) {
    // 禁用基準日期及之前的日期（包含今天）
    return current.isSameOrBefore(base, "day");
  }
  // 禁用基準日期之前的日期（不包含今天）
  return current.isBefore(base, "day");
};

/**
 * 將輸入時間解析為 moment 物件（遵循與 formatToTimestamp 相同的嚴格格式策略）
 */
const parseToMoment = (time: TimeInput): Moment => {
  return timeValidation(time);
};

/**
 * 將無效期時間轉換為 null
 */
const normalizeSentinelDate = (raw: string | number | null | undefined): string | null => {
  if (raw == null) return null;
  if (raw === -1 || raw === "-1") return null;
  const parsed: Moment = typeof raw === "number" ? moment(raw) : moment(raw, supportedFormats, true);
  if (!parsed.isValid()) return null;
  if (parsed.diff(moment(), "years") > SENTINEL_YEARS_THRESHOLD) return null;
  return String(raw);
};

const timeUtils = {
  timestampFormatter,
  formatToTimestamp,
  getDisabledDate,
  parseToMoment,
  normalizeSentinelDate,
};

/** 時間處理工具集：格式化、嚴格解析、禁用日期判斷、防衛性哨兵日期正規化 */
export default timeUtils;
